import type { Accommodation } from '../types';

export const ACCOMMODATIONS: Accommodation[] = [
  {
    id: 'atticus-hotel',
    type: 'accommodation',
    verified: true,
    lastVerified: '2026-04-22',
    region: 'willamette-valley-truffle-corridor',
    name: 'Atticus Hotel',
    description:
      'A downtown McMinnville hotel that works well for travelers who want a walkable town base.',
    address: '375 NE Ford Street',
    city: 'McMinnville',
    state: 'OR',
    lat: 45.2106,
    lng: -123.1978,
    hours: 'Reservations available year-round through the official site.',
    website: 'https://atticushotel.com/',
    phone: '(503) 472-1975',
    highlights: ['Downtown McMinnville', 'Boutique feel', 'Cypress on site', 'Wine-country base'],
    priceRange: '$$$',
    style: 'Boutique Hotel',
    petFriendly: false,
    amenities: ['Downtown location', 'On-site dining', 'Curated design', 'Valet parking'],
    routeNotes: [
      'Useful as a McMinnville base for itineraries centered on downtown dining and walking access.',
      'Relevant for trips that place more of the weekend in town than at a resort.',
    ],
    verificationNote:
      'Hotel identity, address, and on-site dining are verified. Additional experiential claims should be treated cautiously unless they appear on current official pages.',
    sourceUrls: ['https://atticushotel.com/', 'https://atticushotel.com/restaurant/cypress/'],
  },
  {
    id: 'the-allison-inn-spa',
    type: 'accommodation',
    verified: true,
    lastVerified: '2026-04-22',
    region: 'willamette-valley-truffle-corridor',
    name: 'The Allison Inn & Spa',
    description:
      'A Newberg resort with a spa, on-site dining, and a more resort-oriented stay format.',
    address: '2525 Allison Lane',
    city: 'Newberg',
    state: 'OR',
    lat: 45.3197,
    lng: -122.9449,
    hours: 'Reservations available year-round through the official site.',
    website: 'https://theallison.com/',
    phone: '(503) 554-2525',
    highlights: ['Luxury resort', 'JORY Restaurant', 'Spa', 'Wine-country setting'],
    priceRange: '$$$$',
    style: 'Resort',
    petFriendly: false,
    amenities: ['Spa', 'JORY Restaurant', 'Garden and valley views', 'Luxury rooms and suites'],
    routeNotes: [
      'Useful as a Newberg stay option for travelers who want a resort-style base.',
      'Can be paired with other Newberg-area stops in the guide.',
    ],
    verificationNote:
      'Property identity, address, and JORY integration are verified. Package-level or truffle-specific stay claims still need separate confirmation.',
    sourceUrls: ['https://theallison.com/', 'https://theallison.com/resort/directions/'],
  },
  {
    id: 'third-street-flats',
    type: 'accommodation',
    verified: false,
    lastVerified: '2026-04-22',
    region: 'willamette-valley-truffle-corridor',
    name: '3rd Street Flats',
    description:
      'A McMinnville lodging option connected to the Atticus hospitality group with a more apartment-style format.',
    address: '219 NE Cowls Street / 555 NE Third Street',
    city: 'McMinnville',
    state: 'OR',
    lat: 45.2102,
    lng: -123.1985,
    hours: 'Reservations handled through the property site.',
    website: 'https://thirdstreetflats.com/contact-us/',
    phone: '(503) 857-6248',
    highlights: ['McMinnville lodging', 'Apartment-style stay', 'Downtown access'],
    priceRange: '$$',
    style: 'Inn',
    petFriendly: false,
    amenities: ['Downtown location', 'Longer-stay fit', 'Multiple building options'],
    routeNotes: [
      'Keep as a secondary draft option for travelers who want more space and a different stay format.',
    ],
    verificationNote:
      'Property exists and is part of the local lodging mix, but it still needs a clearer guide fit before moving into the main public stay list.',
    sourceUrls: ['https://thirdstreetflats.com/contact-us/'],
  },
  {
    id: 'inn-at-the-5th-eugene',
    type: 'accommodation',
    verified: true,
    lastVerified: '2026-04-23',
    region: 'eugene-festival-truffle-zone',
    name: 'Inn at the 5th Eugene',
    description:
      'A downtown Eugene hotel at 5th Street Public Market with a restaurant-and-market setting that fits a dining-oriented version of the southern route.',
    address: '205 East 6th Avenue',
    city: 'Eugene',
    state: 'OR',
    lat: 44.052,
    lng: -123.0896,
    hours: 'Reservations available through the official hotel site.',
    website: 'https://www.innat5th.com/',
    phone: '(541) 743-4099',
    highlights: ['5th Street Public Market', 'Marché nearby', 'Spa access', 'Downtown Eugene'],
    priceRange: '$$$$',
    style: 'Boutique Hotel',
    petFriendly: true,
    amenities: ['Downtown location', 'Market access', 'Spa', 'Cruiser bikes'],
    routeNotes: [
      'Useful as a Eugene stay tied to dining, market access, and a city-based southern route.',
      'Fits best when the Eugene leg is framed around event travel plus downtown food access.',
    ],
    verificationNote:
      'Official pages support the hotel, 5th Street Public Market location, Marché partnership, spa access, and cruiser bikes. Truffle-menu language should still be verified separately before use.',
    sourceUrls: [
      'https://www.innat5th.com/',
      'https://www.innat5th.com/about-inn-at-the-5th',
      'https://www.innat5th.com/about-inn-at-the-5th/gervais-salon-day-spa',
    ],
  },
  {
    id: 'the-gordon-hotel-eugene',
    type: 'accommodation',
    verified: true,
    lastVerified: '2026-04-23',
    region: 'eugene-festival-truffle-zone',
    name: 'The Gordon Hotel',
    description:
      'An art-forward downtown Eugene hotel that works as a contemporary base for restaurant, market, and festival-oriented travel.',
    address: '555 Oak Street',
    city: 'Eugene',
    state: 'OR',
    lat: 44.0537,
    lng: -123.0908,
    hours: 'Reservations available through the official hotel site.',
    website: 'https://www.thegordonhotel.com/',
    phone: '(541) 762-0555',
    highlights: ['Downtown Eugene', 'Gordon Tavern', 'Complimentary bikes', '5th Street Public Market area'],
    priceRange: '$$$',
    style: 'Boutique Hotel',
    petFriendly: false,
    amenities: ['Downtown location', 'On-site dining', 'Rooftop dining', 'Complimentary cruiser bikes'],
    routeNotes: [
      'Useful as a Eugene stay for travelers who want a central, restaurant-linked base.',
      'Pairs more naturally with an urban Eugene itinerary than with a resort-style route.',
    ],
    verificationNote:
      'Official pages support the hotel, on-site dining, rooftop venue, art-forward positioning, and complimentary bikes. It works best in the guide as an Eugene event-and-city-base option rather than as a truffle-specific destination stay.',
    sourceUrls: [
      'https://www.thegordonhotel.com/information/our-hotel',
      'https://www.thegordonhotel.com/dining',
      'https://www.thegordonhotel.com/rooms-suites',
    ],
  },
  {
    id: 'sentinel-hotel-portland',
    type: 'accommodation',
    verified: true,
    lastVerified: '2026-04-23',
    region: 'portland-foothills-truffle-zone',
    name: 'Sentinel Hotel',
    description:
      'A downtown Portland hotel with on-site dining and wine-lounge options that fits a city-base version of the Portland foothills route.',
    address: '614 SW 11th Avenue',
    city: 'Portland',
    state: 'OR',
    lat: 45.5229,
    lng: -122.6824,
    hours: 'Reservations available through the official hotel site.',
    website: 'https://www.sentinelhotel.com/',
    phone: '(503) 224-3400',
    highlights: ['Downtown Portland', "Jake's Grill", 'Wine lounge', 'Historic hotel'],
    priceRange: '$$$$',
    style: 'Boutique Hotel',
    petFriendly: true,
    amenities: ['Downtown location', 'On-site dining', 'Wine lounge', 'Central city base'],
    routeNotes: [
      'Useful as a Portland base for readers planning a guided foothills outing and a restaurant-led evening back in town.',
      'Fits the city-stay version of the route better than an outward-facing resort or lodge framing.',
    ],
    verificationNote:
      'Official pages support the downtown location, on-site dining, wine lounge, and hotel identity. The guide fit is about Portland base logistics and dining access, not any truffle-specific hotel program.',
    sourceUrls: [
      'https://www.sentinelhotel.com/',
      'https://www.sentinelhotel.com/dining/jakes-grill/',
      'https://www.sentinelhotel.com/dining/domaine-serene-wine-lounge/',
    ],
  },
  {
    id: 'the-nines-portland',
    type: 'accommodation',
    verified: true,
    lastVerified: '2026-04-23',
    region: 'portland-foothills-truffle-zone',
    name: 'The Nines, Portland',
    description:
      'A central downtown Portland hotel that works as a polished overnight base for travelers pairing a foothills outing with city dining and an easier car-light stay.',
    address: '525 SW Morrison Street',
    city: 'Portland',
    state: 'OR',
    lat: 45.5187,
    lng: -122.6784,
    hours: 'Reservations available through the official hotel site.',
    website: 'https://www.thenines.com/',
    phone: '(503) 222-9996',
    highlights: ['Downtown Portland', 'Two restaurants', 'Historic building', 'Central transit access'],
    priceRange: '$$$$',
    style: 'Boutique Hotel',
    petFriendly: true,
    amenities: ['Downtown location', 'On-site dining', 'Central city base', 'Historic building'],
    routeNotes: [
      'Useful as a Portland stay for readers who want an urban overnight before or after a seasonal foothills outing.',
      'Works best when the route is framed as Portland basecamp plus one guided truffle day rather than a rural lodge trip.',
    ],
    verificationNote:
      'Official pages support the downtown location, two restaurants, and hotel amenities. It belongs in the guide as a city-base option, not as a truffle-specific destination property.',
    sourceUrls: [
      'https://www.thenines.com/',
      'https://www.thenines.com/contact/',
    ],
  },
  {
    id: 'graduate-by-hilton-eugene',
    type: 'accommodation',
    verified: false,
    lastVerified: '2026-04-23',
    region: 'eugene-festival-truffle-zone',
    name: 'Graduate by Hilton Eugene',
    description:
      'A downtown Eugene hotel with on-site dining and a large full-service footprint that could support festival and event-driven stays.',
    address: '66 E 6th Ave',
    city: 'Eugene',
    state: 'OR',
    lat: 44.0534,
    lng: -123.0901,
    hours: 'Reservations available through the official hotel site.',
    website: 'https://www.hilton.com/en/hotels/euggegu-graduate-eugene/',
    phone: '(541) 342-2000',
    highlights: ['Downtown Eugene', 'Trophy Room', 'Poindexter Coffee', 'Indoor pool'],
    priceRange: '$$$',
    style: 'Boutique Hotel',
    petFriendly: true,
    amenities: ['On-site dining', 'Lobby cafe', 'Indoor pool', 'Downtown location'],
    routeNotes: [
      'Useful as a Eugene draft stay for event travelers who want a larger downtown hotel.',
      'Fits better as a festival and city-base option than as a truffle-specific destination stay.',
    ],
    verificationNote:
      'Official Hilton pages support the downtown location, on-site dining, cafe, and hotel amenities. The truffle route case is still more practical than distinctive, so it stays in draft for now.',
    sourceUrls: [
      'https://www.hilton.com/en/hotels/euggegu-graduate-eugene/',
      'https://www.hilton.com/en/hotels/euggegu-graduate-eugene/dining/',
      'https://www.hilton.com/en/hotels/euggegu-graduate-eugene/hotel-location/',
    ],
  },
  {
    id: 'courtyard-by-marriott-corvallis',
    type: 'accommodation',
    verified: false,
    lastVerified: '2026-04-23',
    region: 'corvallis-truffle-science-zone',
    name: 'Courtyard by Marriott Corvallis',
    description:
      'A riverfront Corvallis hotel with modern rooms and on-site dining that fits the practical side of an academic or workshop-driven trip.',
    address: '400 SW 1st Street',
    city: 'Corvallis',
    state: 'OR',
    lat: 44.5627,
    lng: -123.2608,
    hours: 'Reservations available through the official hotel site.',
    website: 'https://www.marriott.com/en-us/hotels/eugco-courtyard-corvallis/overview/',
    phone: '(541) 753-0199',
    highlights: ['Downtown Corvallis', 'The Bistro', 'Indoor pool', 'OSU access'],
    priceRange: '$$$',
    style: 'Inn',
    petFriendly: false,
    amenities: ['On-site dining', 'Indoor pool', 'Business center', 'Downtown access'],
    routeNotes: [
      'Useful as a Corvallis draft stay for workshop, conference, or training-oriented trips.',
      'Fits the practical side of the academic zone better than a destination-luxury framing.',
    ],
    verificationNote:
      'Official Marriott pages support the riverfront location, OSU access, indoor pool, and on-site dining. The guide fit is credible, but the public academic zone still needs more complete traveler-facing structure.',
    sourceUrls: [
      'https://www.marriott.com/en-us/hotels/eugco-courtyard-corvallis/overview/',
      'https://www.marriott.com/en-us/hotels/eugco-courtyard-corvallis/dining/',
      'https://www.marriott.com/en-us/hotels/eugco-courtyard-corvallis/rooms/',
    ],
  },
  {
    id: 'hilton-garden-inn-corvallis',
    type: 'accommodation',
    verified: true,
    lastVerified: '2026-04-23',
    region: 'corvallis-truffle-science-zone',
    name: 'Hilton Garden Inn Corvallis',
    description:
      'An OSU-campus hotel that fits the conference, workshop, and research side of a Corvallis-based truffle trip.',
    address: '2500 SW Western Boulevard',
    city: 'Corvallis',
    state: 'OR',
    lat: 44.5639,
    lng: -123.2776,
    hours: 'Reservations available through the official hotel site.',
    website: 'https://www.hilton.com/en/hotels/cvochgi-hilton-garden-inn-corvallis/',
    phone: '(541) 752-5000',
    highlights: ['OSU campus', 'Stadium Bar and Grill', 'Indoor pool', 'Conference access'],
    priceRange: '$$$',
    style: 'Inn',
    petFriendly: true,
    amenities: ['On-site restaurant', 'Indoor pool', 'Fitness center', 'Campus location'],
    routeNotes: [
      'Useful as a Corvallis stay for academic travel, conference-style events, and society meetings tied to the research side of the guide.',
      'More practical than experiential, which fits the academic leg better than a destination-luxury framing.',
    ],
    verificationNote:
      'Official Hilton pages support the OSU-campus location, on-site restaurant, and indoor pool. It works in the guide as a practical base for the academic leg rather than as a truffle-specific destination property.',
    sourceUrls: [
      'https://www.hilton.com/en/hotels/cvochgi-hilton-garden-inn-corvallis/',
    ],
  },
];
